import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../../legacy/services/apiClient";
import footerData from "../../legacy/data/footerOriginalV1.json";
import { hasFooterContent, normalizePublicFooterPayload } from "../../legacy/utils/publicFooter";
import { useCurrency } from "../../legacy/components/CurrencyContext";

const fallbackFooter = normalizePublicFooterPayload(footerData);
const badContactPattern = /support@homevilas|99999|124 400|gurugram/i;
const requiredPhones = ["+91 8287703118"];
const requiredEmails = ["info@homevilas.com", "meena@homevilas.com"];
const requiredCompanyLinks = [
  { label: "About", url: "/about" },
  { label: "Careers", url: "/careers" },
  { label: "Privacy Policy", url: "/privacy" },
  { label: "Blog", url: "/blog" },
  { label: "Partner with us", url: "/partner-with-us" },
];
const requiredSupportLinks = [
  { label: "Help Center", url: "/help" },
  { label: "Contact", url: "/contact" },
  { label: "Terms & Conditions", url: "/terms" },
  { label: "Privacy", url: "/privacy" },
];
const requiredPlaces = [
  "Goa",
  "Rajasthan",
  "Gujarat",
  "Kerala",
  "Maharashtra",
  "Himachal Pradesh",
  "Uttarakhand",
  "Delhi",
  "Mumbai",
  "Jaipur",
  "Udaipur",
  "Agra",
  "Rishikesh",
  "Alibaug",
  "Lonavala",
  "Bengaluru",
].map((label) => ({
  label,
  url: `/listings?location=${encodeURIComponent(label)}`,
}));

const telHref = (phone = "") => {
  const digits = String(phone).replace(/[^\d]/g, "");
  if (!digits) return "";
  const normalized = digits.length === 10 ? `91${digits}` : digits;
  return `tel:+${normalized}`;
};

const splitValues = (value = "") =>
  String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const uniqueValues = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item || "").replace(/\s+/g, "").toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mergeLinks = (primary = [], required = []) => {
  const seen = new Set();
  return [...primary, ...required].filter((link) => {
    const label = String(link?.label || "").trim();
    if (!label) return false;
    const key = label.toLowerCase().replace(/\s+/g, " ");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const iconForSocial = (platform = "") => {
  const key = String(platform).toLowerCase();
  if (key.includes("facebook")) return "bi-facebook";
  if (key.includes("youtube")) return "bi-youtube";
  if (key.includes("whatsapp")) return "bi-whatsapp";
  return "bi-instagram";
};

export default function MobileFooter() {
  const [remoteFooter, setRemoteFooter] = useState(null);
  const [openSection, setOpenSection] = useState("places");
  const { currency, setCurrency, supportedCurrencies } = useCurrency();

  useEffect(() => {
    let active = true;
    apiGet("/api/public/footer")
      .then((payload) => {
        if (active) setRemoteFooter(normalizePublicFooterPayload(payload));
      })
      .catch(() => {
        if (active) setRemoteFooter(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const footer = useMemo(() => {
    if (!hasFooterContent(remoteFooter)) return fallbackFooter;
    const next = { ...fallbackFooter, ...remoteFooter };
    if (!next.contact_email || badContactPattern.test(next.contact_email)) {
      next.contact_email = fallbackFooter.contact_email;
    }
    if (!next.contact_phone || badContactPattern.test(next.contact_phone)) {
      next.contact_phone = fallbackFooter.contact_phone;
    }
    if (!next.contact_address || badContactPattern.test(next.contact_address)) {
      next.contact_address = fallbackFooter.contact_address;
    }
    next.company_links = remoteFooter.company_links?.length ? remoteFooter.company_links : fallbackFooter.company_links;
    next.support_links = remoteFooter.support_links?.length ? remoteFooter.support_links : fallbackFooter.support_links;
    next.places_links = remoteFooter.places_links?.length ? remoteFooter.places_links : fallbackFooter.places_links;
    return next;
  }, [remoteFooter]);

  const companyLinks = mergeLinks(footer.company_links || [], requiredCompanyLinks).slice(0, 5);
  const supportLinks = mergeLinks(footer.support_links || [], requiredSupportLinks).slice(0, 4);
  const placeLinks = mergeLinks(footer.places_links || [], requiredPlaces).slice(0, 16);
  const emails = uniqueValues([...splitValues(footer.contact_email), ...requiredEmails]);
  const primaryPhone = requiredPhones[0];
  const primaryEmail = emails[0] || requiredEmails[0];
  const phoneHref = telHref(primaryPhone);
  const whatsappPhone = phoneHref.replace(/^tel:\+?/, "");
  const nonWhatsappSocials = (footer.social_links || []).filter(
    (item) => !String(item?.platform || item?.label || item?.url || "").toLowerCase().includes("whatsapp")
  );
  const socials = [
    ...nonWhatsappSocials,
    whatsappPhone ? { platform: "whatsapp", label: "WhatsApp", url: `https://wa.me/${whatsappPhone}` } : null,
  ].filter(Boolean);
  const copyright = footer.copyright_text || `© ${new Date().getFullYear()} Homevilas. All rights reserved.`;
  const normalizedCopyright = copyright.replace("Â©", "©").replace(/^(\d{4})\s*©\s*/i, "© $1 ");

  const toggleSection = (section) => {
    setOpenSection((current) => (current === section ? "" : section));
  };

  const renderAccordion = (key, title, icon, links, panel) => {
    const isOpen = openSection === key;
    return (
      <article className={`hv-mobile-footer__accordion${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="hv-mobile-footer__accordion-trigger"
          aria-expanded={isOpen}
          onClick={() => toggleSection(key)}
        >
          <span className="hv-mobile-footer__accordion-title">
            <span className="hv-mobile-footer__accordion-icon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
            {title}
          </span>
          <span className="hv-mobile-footer__accordion-chevron"><i className="bi bi-chevron-down" aria-hidden="true" /></span>
        </button>
        <div className="hv-mobile-footer__accordion-panel">
          {panel || links.map((link) => <a href={link.url || "#"} key={`${key}-${link.label}-${link.url}`}>{link.label}</a>)}
        </div>
      </article>
    );
  };

  return (
    <footer className="hv-mobile-footer">
      <div className="hv-mobile-footer__container">
        <section className="hv-mobile-footer__brand">
          <a href="/" className="hv-mobile-footer__logo" aria-label="Homevilas Home">
            <span className="hv-mobile-footer__logo-mark">H</span>
            <span className="hv-mobile-footer__logo-name">{footer.brand_name || "Homevilas"}</span>
          </a>
          <p className="hv-mobile-footer__description">
            {footer.about_text || footer.brand_tagline || "Luxury stays, curated tours, and premium outstation cab services for memorable journeys."}
          </p>
        </section>

        <section className="hv-mobile-footer__quick-actions" aria-label="Quick contact">
          <a href={phoneHref || "#"} className="hv-mobile-footer__quick-card" aria-label="Call Homevilas">
            <span className="hv-mobile-footer__quick-icon"><i className="bi bi-telephone-fill" aria-hidden="true" /></span>
            <span className="hv-mobile-footer__quick-copy"><small>Call us</small><strong>{primaryPhone}</strong></span>
          </a>
          <a href={`mailto:${primaryEmail}`} className="hv-mobile-footer__quick-card" aria-label="Email Homevilas">
            <span className="hv-mobile-footer__quick-icon"><i className="bi bi-envelope-fill" aria-hidden="true" /></span>
            <span className="hv-mobile-footer__quick-copy"><small>Email us</small><strong>{primaryEmail}</strong></span>
          </a>
        </section>

        <section className="hv-mobile-footer__address">
          <span className="hv-mobile-footer__address-icon"><i className="bi bi-geo-alt-fill" aria-hidden="true" /></span>
          <span>{footer.contact_address || "E-6, Kalkaji, New Delhi-110019"}</span>
        </section>

        <section className="hv-mobile-footer__contact-extra" aria-label="Additional contact details">
          {emails.slice(1).map((email) => <a href={`mailto:${email}`} key={email}>{email}</a>)}
        </section>

        <section className="hv-mobile-footer__accordions">
          {renderAccordion("company", "Company", "bi-bank2", companyLinks)}
          {renderAccordion("support", "Support", "bi-headset", supportLinks)}
          {renderAccordion("places", "Popular Places", "bi-geo-alt-fill", placeLinks, (
            <div className="hv-mobile-footer__place-grid">
              {placeLinks.map((link) => <a href={link.url || "#"} key={`place-${link.label}-${link.url}`}>{link.label}</a>)}
            </div>
          ))}
        </section>

        <section className="hv-mobile-footer__preferences">
          <label className="hv-mobile-footer__preference" aria-label="Choose currency">
            <span className="hv-mobile-footer__preference-icon"><i className="bi bi-globe2" aria-hidden="true" /></span>
            <span>Currency</span>
            <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              {supportedCurrencies.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </label>
          <button type="button" className="hv-mobile-footer__preference" aria-label="Language English" aria-disabled="true">
            <span className="hv-mobile-footer__preference-icon"><i className="bi bi-translate" aria-hidden="true" /></span>
            <span>Language: English</span>
            <span className="hv-mobile-footer__preference-chevron"><i className="bi bi-chevron-down" aria-hidden="true" /></span>
          </button>
        </section>

        {socials.length ? (
          <section className="hv-mobile-footer__socials" aria-label="Social links">
            {socials.slice(0, 4).map((item) => (
              <a href={item.url || "#"} key={`${item.platform}-${item.url}`} aria-label={item.label || item.platform} target="_blank" rel="noreferrer">
                <i className={`bi ${iconForSocial(item.platform || item.label)}`} aria-hidden="true" />
              </a>
            ))}
          </section>
        ) : null}

        <section className="hv-mobile-footer__bottom">
          <p>{normalizedCopyright}</p>
          <nav className="hv-mobile-footer__legal" aria-label="Legal links">
            <a href="/terms">Terms & Conditions</a>
            <span aria-hidden="true">|</span>
            <a href="/privacy">Privacy Policy</a>
          </nav>
        </section>
      </div>
    </footer>
  );
}
